import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteDataContext = createContext(null);

export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within SiteDataProvider');
  }
  return context;
};

export const SiteDataProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [social, setSocial] = useState([]);
  const [references, setReferences] = useState([]);
  const [featuredReferences, setFeaturedReferences] = useState([]);
  const [services, setServices] = useState([]);
  const [logos, setLogos] = useState([]);
  const [pages, setPages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [contentRes, projectsRes, socialRes, referencesRes, featuredRefsRes, servicesRes, logosRes, pagesRes, faqsRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/social`),
          axios.get(`${API}/references`).catch(() => ({ data: [] })),
          axios.get(`${API}/references/featured`).catch(() => ({ data: [] })),
          axios.get(`${API}/services`),
          axios.get(`${API}/logos`).catch(() => ({ data: [] })),
          axios.get(`${API}/pages`).catch(() => ({ data: [] })),
          axios.get(`${API}/faqs`).catch(() => ({ data: [] }))
        ]);
        setContent(contentRes.data);
        setProjects(projectsRes.data);
        setSocial(socialRes.data);
        setReferences(referencesRes.data || []);
        setFeaturedReferences(featuredRefsRes.data || []);
        setServices(servicesRes.data);
        setLogos(logosRes.data || []);
        setPages(pagesRes.data || []);
        setFaqs(faqsRes.data || []);
      } catch (err) {
        console.error('Failed to load site data:', err);
        setContent({
          brand_name: "nyza-studio",
          hero_subtitle: "WHERE PIXELS MEET MAGIC 🪄",
          hero_location: "Based in Austria",
          portfolio_title: "Portfolio",
          portfolio_subtitle: "Selected projects featuring motion design, video editing, and creative direction.",
          portfolio_websites_title: "Website Projects",
          portfolio_websites_subtitle: "Hover to scroll through my work",
          portfolio_social_title: "Social Media & Content",
          portfolio_social_subtitle: "Animations, CGI & Videos",
          services_title: "Services",
          services_subtitle: "I focus on projects where I'm confident we can create a strong result.",
          contact_title: "Contact",
          contact_whatsapp: "4900000000",
          contact_email: "workwith@nyza-studio.at",
          footer_copyright: "NYZA Studio",
          footer_instagram: "https://instagram.com/nyza.studio",
          footer_youtube: "https://youtube.com/@nyzastudio",
          footer_linkedin: "https://linkedin.com/company/nyza-studio"
        });
        setProjects([]);
        setSocial([]);
        setReferences([]);
        setFeaturedReferences([]);
        setServices([]);
        setLogos([]);
        setPages([]);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <SiteDataContext.Provider value={{ content, projects, social, references, featuredReferences, services, logos, pages, faqs, loading }}>
      {children}
    </SiteDataContext.Provider>
  );
};
